/**
 * @Author: 李旭
 * @Date: 2021-12-03 14:03:41
 * @LastEditTime: 2024-10-12 17:02:21
 * @LastEditors: lixu
 * @Description: 公共部分或者全局需要用到的api
 * @FilePath: \ems-web-ant5.0\src\services\global.js
 * 可以输入预定的版权声明、个性签名、空行等
 */
import request from '@/utils/request';
import {
  FIXED_ADMIN_LOGIN,
  FIXED_ADMIN_MENUS,
  FIXED_ADMIN_USER,
  FIXED_TENANT_MENUS,
  FIXED_TENANT_LOGIN,
  FIXED_TENANT_USER,
} from '@/constants/mockAuthV2';
import * as storage from '@/utils/storage';

const ENABLE_LOCAL_MENU_MOCK = false;
const ENABLE_LOCAL_LOGIN_MOCK = false;

type LoginPayload = {
  userName?: string;
  password?: string;
  captchaVerifyParam?: string;
};

type LoginApiResult = {
  code: string;
  message: string;
  result: Record<string, unknown>;
};

export interface LoginCaptchaStatus {
  enabled: boolean;
  captchaRequired: boolean;
  captchaType: 'traceless' | 'slider';
  failureCount: number;
  failureThreshold: number;
  expiresInSeconds: number;
  region: string;
  prefix: string;
  sceneId: string;
}

// 这两个匿名登录接口自行展示错误，保留 HTTP 错误响应中的后端提示。
const loginRequestErrorHandler = (fallback: string) => (error: { data?: unknown }) => {
  const message = isObject(error.data) && typeof error.data.message === 'string'
    ? error.data.message
    : '';
  throw new Error(message || fallback);
};

/** 登录前查询当前客户端的验证码要求，由后端统一维护失败次数。 */
export async function getLoginCaptchaStatus(): Promise<LoginCaptchaStatus> {
  const response = await request('/user/login/captcha/status', {
    method: 'POST',
    skipErrorMessage: true,
    timeout: 15000,
    errorHandler: loginRequestErrorHandler('安全验证状态查询失败，请稍后重试'),
  });
  if (!isObject(response) || response.code !== '00000' || !isObject(response.result)) {
    throw new Error(isObject(response) && typeof response.message === 'string' && response.message
      ? response.message : '安全验证状态查询失败，请稍后重试');
  }
  const status = response.result;
  if (typeof status.enabled !== 'boolean' || typeof status.captchaRequired !== 'boolean') {
    throw new Error('安全验证状态异常，请稍后重试');
  }
  if (status.enabled && status.captchaType !== 'slider' && status.captchaType !== 'traceless') {
    throw new Error('验证码类型配置异常，请联系管理员');
  }
  return status as unknown as LoginCaptchaStatus;
}

type CommonActionResult = {
  code: string;
  message: string;
  result?: unknown;
};

export type MenuNode = {
  menuId?: number;
  pid?: number;
  level?: number;
  menuName?: string;
  menuNameEn?: string;
  name?: string;
  path?: string;
  component?: string;
  icon?: string;
  orderRule?: number;
  useType?: number;
  status?: number;
  children?: MenuNode[];
  routes?: MenuNode[];
};

type MenuApiResult = {
  code: string;
  message: string;
  result: MenuNode[];
};

const LOGIN_SUCCESS_CODE = '00000';
const LOGIN_FAILED_CODE = 'LOGIN_FAILED';
const DEV_FORCE_MENU_PATH = '/system/menu-manage';
const DEV_FORCE_MENU_COMPONENT = './SystemManage/MenuManage';

const isDevRuntime =
  process.env.NODE_ENV === 'development' ||
  process.env.UMI_ENV === 'dev' ||
  process.env.APP_ENV === 'dev';

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const normalizeLoginResponse = (payload: unknown): LoginApiResult => {
  // 严格按后端阿里规范 RestResult 解析：code===00000 才是成功
  if (isObject(payload) && 'code' in payload && 'message' in payload && 'result' in payload) {
    const code = String(payload.code ?? '');
    const message = String(payload.message ?? '');
    const result = isObject(payload.result) ? payload.result : {};

    if (code === LOGIN_SUCCESS_CODE) {
      return {
        code,
        message,
        result,
      };
    }

    return {
      code: code || LOGIN_FAILED_CODE,
      message: message || '登录失败',
      result: {},
    };
  }
  // 非规范结构一律按失败处理，避免误登录
  const message = isObject(payload)
    ? String(payload.message ?? '登录失败，请检查账号或密码')
    : '登录失败，请检查账号或密码'; /*
    ? String(payload.message ?? '登录响应格式不正确')
    : '登录响应格式不正确'; /*
    ? String(payload.message ?? '登录响应格式不正确')
    : '登录响应格式不正确';
  */
  const code = isObject(payload) ? String(payload.code ?? LOGIN_FAILED_CODE) : LOGIN_FAILED_CODE;
  return {
    code: code || LOGIN_FAILED_CODE,
    message,
    result: {},
  };
};

const normalizeMenuNode = (node: MenuNode): MenuNode => {
  const children = Array.isArray(node.children) ? node.children : [];
  const routes = Array.isArray(node.routes) ? node.routes : [];
  const nextChildren = children.length > 0 ? children : routes;
  const normalizedChildren = nextChildren.map((item) => normalizeMenuNode(item));

  return {
    ...node,
    name: node.name || node.menuName || node.menuNameEn || '',
    routes: normalizedChildren,
    children: normalizedChildren,
  };
};

const normalizeActionResponse = (
  payload: unknown,
  fallbackMessage = 'success',
): CommonActionResult => {
  if (isObject(payload) && 'code' in payload) {
    return {
      code: String(payload.code ?? LOGIN_FAILED_CODE),
      message: String(payload.message ?? fallbackMessage),
      result: 'result' in payload ? payload.result : null,
    };
  }

  return {
    code: LOGIN_SUCCESS_CODE,
    message: fallbackMessage,
    result: payload ?? null,
  };
};

const resolveUserIdParams = (userId?: string | number) => {
  const id = Number(userId);
  if (Number.isFinite(id) && id > 0) {
    return { userId: id };
  }
  return undefined;
};

const hasMenuPath = (menus: MenuNode[], path: string): boolean => {
  for (const item of menus) {
    if (item.path === path) return true;
    const children = Array.isArray(item.routes)
      ? item.routes
      : Array.isArray(item.children)
      ? item.children
      : [];
    if (children.length && hasMenuPath(children, path)) {
      return true;
    }
  }
  return false;
};

const getMenuName = (menu: MenuNode) => String(menu.name || menu.menuName || menu.menuNameEn || '');

const ensureDevMenuManage = (menus: MenuNode[]): MenuNode[] => {
  if (!isDevRuntime) return menus;
  if (hasMenuPath(menus, DEV_FORCE_MENU_PATH)) return menus;

  const menuManageNode: MenuNode = {
    menuId: -900001,
    pid: -900000,
    level: 2,
    menuName: '菜单管理',
    menuNameEn: 'menuManage',
    name: '菜单管理',
    path: DEV_FORCE_MENU_PATH,
    component: DEV_FORCE_MENU_COMPONENT,
    icon: 'smile',
    orderRule: 9999,
    useType: 1,
    status: 1,
    routes: [],
    children: [],
  };

  const nextMenus = [...menus];
  const systemIndex = nextMenus.findIndex(
    (item) => item.path === '/system' || getMenuName(item).includes('系统')
  );

  if (systemIndex >= 0) {
    const systemNode = normalizeMenuNode(nextMenus[systemIndex]);
    const childList = [...(systemNode.routes || systemNode.children || [])];
    childList.push(menuManageNode);
    nextMenus[systemIndex] = {
      ...systemNode,
      routes: childList,
      children: childList,
    };
    return nextMenus;
  }

  nextMenus.push({
    menuId: -900000,
    pid: 0,
    level: 1,
    menuName: '系统管理',
    menuNameEn: 'systemManage',
    name: '系统管理',
    path: '/system',
    icon: 'smile',
    orderRule: 9998,
    useType: 1,
    status: 1,
    routes: [menuManageNode],
    children: [menuManageNode],
  });

  return nextMenus;
};

const extractMenuList = (result: unknown): MenuNode[] => {
  if (Array.isArray(result)) return result as MenuNode[];
  if (isObject(result) && Array.isArray(result.result)) {
    return result.result as MenuNode[];
  }
  if (isObject(result) && isObject(result.data) && Array.isArray(result.data.result)) {
    return result.data.result as MenuNode[];
  }
  return [];
};

export const getFirstMenuPath = (menus: MenuNode[]): string | undefined => {
  for (const item of menus) {
    const children = Array.isArray(item.routes)
      ? item.routes
      : Array.isArray(item.children)
      ? item.children
      : [];
    const childPath = children.length ? getFirstMenuPath(children) : undefined;
    if (childPath) return childPath;
    if (item.path) return item.path;
  }
  return undefined;
};

const normalizeMenusResponse = (payload: unknown): MenuApiResult => {
  if (isObject(payload) && 'code' in payload && 'message' in payload) {
    const code = String(payload.code ?? '');
    const message = String(payload.message ?? '');
    const result = ensureDevMenuManage(
      extractMenuList(payload.result).map((item) => normalizeMenuNode(item))
    );

    return {
      code: code || LOGIN_FAILED_CODE,
      message,
      result,
    };
  }

  return {
    code: LOGIN_FAILED_CODE, /*
    message: '菜单响应格式不正确',
    
    message: '菜单响应格式不正确',
    */
    message: 'menu response format error',
    result: [],
  };
};

/**
 * @description: 获取用户的菜单列表
 * @param {*}
 * @return {Promise}
 */
export async function getMenus() {
  if (ENABLE_LOCAL_MENU_MOCK) {
    const currentUser = (storage.get('current-user') || {}) as Record<string, unknown>;
    const userType = String(currentUser.userType ?? '');
    const roleCode = String(currentUser.roleCode ?? '').toUpperCase();
    const access = String(currentUser.access ?? '').toLowerCase();
    const userName = String(currentUser.userName ?? '').toLowerCase();
    const useAdminMenu =
      userType === '1' ||
      roleCode.includes('PLATFORM') ||
      access === 'admin' ||
      userName === FIXED_ADMIN_LOGIN.userName.toLowerCase();

    const result = ensureDevMenuManage(useAdminMenu ? FIXED_ADMIN_MENUS : FIXED_TENANT_MENUS);

    return Promise.resolve({
      code: '00000',
      message: 'success',
      result,
    });
  }

  const response = await request('/user/auth/menus', {
    method: 'GET',
  });
  return normalizeMenusResponse(response);
}
/**
 * @description: 用户登录
 * @param {*} params
 * @return {*}
 */
export async function accountLogin(params: LoginPayload): Promise<LoginApiResult> {
  if (ENABLE_LOCAL_LOGIN_MOCK) {
    const userName = params?.userName?.trim();
    const isTenantLogin = userName === FIXED_TENANT_LOGIN.userName;
    const isAdminLogin =
      userName === FIXED_ADMIN_LOGIN.userName || !userName;

    const loginResult = isTenantLogin
      ? FIXED_TENANT_USER
      : isAdminLogin
      ? FIXED_ADMIN_USER
      : FIXED_TENANT_USER;

    return Promise.resolve({
      code: '00000',
      message: 'success',
      result: loginResult as unknown as Record<string, unknown>,
    });
  }

  const response = await request('/user/login/api', {
    method: 'POST',
    data: params,
    skipErrorMessage: true,
    errorHandler: loginRequestErrorHandler('登录服务暂不可用，请稍后重试'),
  });

  // 请求层会将 HTTP/网络异常转换成 Response 或 undefined，不能将其计为业务失败。
  if (!isObject(response) || !('code' in response) || !('message' in response)) {
    throw new Error('登录服务暂不可用，请稍后重试');
  }

  const normalized = normalizeLoginResponse(response);

  return {
    code: String(normalized.code ?? ''),
    message: normalized.message ?? '',
    result: normalized.result ?? {},
  };
}
/**
 * @description: 用户退出登录
 * @param {*}
 * @return {*}
 */
export async function loginOut() {
  return request('/logout', {
    method: 'GET',
  });
}

export async function enterTenantContext(tenantId: string | number, userId?: string | number) {
  const parsedTenantId = Number(tenantId);
  const payload = await request('/user/auth/tenant-context', {
    method: 'POST',
    params: resolveUserIdParams(userId),
    data: {
      tenantId: Number.isFinite(parsedTenantId) ? parsedTenantId : tenantId,
    },
  });

  return normalizeActionResponse(payload, '介入租户成功');
}

export async function exitTenantContext(userId?: string | number) {
  const payload = await request('/user/auth/tenant-context/exit', {
    method: 'POST',
    params: resolveUserIdParams(userId),
  });
  /*

  return normalizeActionResponse(payload, '返回管理端成功'); /*

  return normalizeActionResponse(payload, '返回管理端成功');
  */
  return normalizeActionResponse(payload, 'tenant context exit success');
}

/**
 * @description: 修改用户密码
 * @param {*}
 * @return {*}
 */

export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export async function changePassword(data: ChangePasswordPayload): Promise<CommonActionResult> {
  const payload = await request('/user/profile/password', {
    method: 'PUT',
    data: { oldPassword: data.oldPassword, newPassword: data.newPassword },
    errorHandler: (error: { data?: { message?: string } }) => {
      throw new Error(error.data?.message || '修改密码失败，请稍后重试');
    },
  });
  return normalizeActionResponse(payload, '修改密码成功');
}

/**
 * @description: 获取系统字典表
 * @return {*}
 */
export async function getDicList() {
  return request(`/common/dictionaries/dicKeys`, {
    method: 'GET',
    // params: { dicKey },
  });
}

/**
 * @description: 下载excel
 * @param {*} params
 * @return {*}
 */
export async function downloadExcel(url: string, params?: Record<string, unknown>) {
  return request(url, {
    method: 'GET',
    responseType: 'blob',
    getResponse: true,
    params,
  });
}

/**
 * @description: 获取报警信息
 * @param {*}
 * @return {*}
 */
export async function getNoticesList() {
  return request('/notice/list', {
    method: 'GET',
  });
}
/**
 * @description: 从服务器获取当前模式
 * @return {*}
 */
export async function getLocalWarhouse() {
  return request('/config/storagePattern', {
    method: 'GET',
  });
}
/**
 * @description 获取字典表
 * @param {*} dicKey
 * @returns
 */
export async function getDicLists(dicKey: string) {
  return request('/user/commons/dictionaries', {
    method: 'GET',
    params: { dicKey },
  });
}
